import { useTranslation } from '@hooks/useTranslation';
import { POPOUT_CHROME_WIDTH, PopOutList } from '../DataTable/PopOutList';
import { ScrollingContent } from '../DataTable/ClampedTextCell';
import { useClampOverflow } from '../DataTable/useClampOverflow';
import type { Product } from './types';

const textClass = 'font-inter text-sm font-normal leading-[1.5] text-off-black break-words';
// Matches the sales-order Events popout; PopOutList shifts the card up when it would overflow the viewport.
const MAX_EXPANDED_HEIGHT = '350px';

export function EquipmentCell({
  inventory_items,
  container_items,
}: Pick<Product, 'inventory_items' | 'container_items'>) {
  const { t } = useTranslation();
  const { containerRef, previewRef, isTruncated, cellWidth, collapsedHeight } =
    useClampOverflow<HTMLDivElement>([inventory_items, container_items, t]);

  const hasInventory = inventory_items.length > 0;
  const hasContainers = container_items.length > 0;
  if (!hasInventory && !hasContainers) {
    return <span className={textClass}>—</span>;
  }

  const content = (
    <>
      {hasInventory && (
        <div>
          <div className="font-semibold text-grey">{t('index.equipment.inventory_sets')}</div>
          {inventory_items.map((item, index) => (
            <div key={`${item.id}-${index}`}>{item.name} × {item.quantity ?? '—'}</div>
          ))}
        </div>
      )}
      {hasInventory && hasContainers && <hr className="my-3 border-0 border-t border-dinnerplate-white" />}
      {hasContainers && (
        <div>
          <div className="font-semibold text-grey">{t('index.equipment.container_sets')}</div>
          {[...container_items]
            .sort((a, b) => (a.minimum_pax_required ?? 0) - (b.minimum_pax_required ?? 0))
            .map((item, index) => (
              <div key={`${item.id}-${index}`}>
                {item.name} ({item.minimum_pax_required == null
                  ? t('index.equipment.up_to_pax', { capacity: item.capacity })
                  : t('index.equipment.pax_range', { minimum: item.minimum_pax_required, capacity: item.capacity })})
              </div>
            ))}
        </div>
      )}
    </>
  );
  const preview = (
    <div ref={previewRef} className="overflow-hidden" style={{ maxHeight: collapsedHeight }}>
      {content}
    </div>
  );

  return (
    <div ref={containerRef} className={textClass}>
      {isTruncated ? (
        <PopOutList
          width={cellWidth + POPOUT_CHROME_WIDTH}
          collapsedHeight={collapsedHeight}
          collapsed={preview}
          expanded={
            <ScrollingContent className={textClass} maxHeight={MAX_EXPANDED_HEIGHT}>
              {content}
            </ScrollingContent>
          }
        />
      ) : preview}
    </div>
  );
}
